<template>
  <div
    class="search-block container"
    :style="{
      padding: blockPadding
    }"
  >
    <v-search
      :placeholder="$t('components.blocks.searchBlock.searchPlaceholder')"
      class="search-block__button"
      @click="openSearchModal"
    />

    <v-modal @open="handleModalOpened">
      <template :slot="searchHash">
        <action-header no-border>
          <div
            class="w-100 d-flex justify-content-between align-items-center px-1"
          >
            {{ $t("components.blocks.searchBlock.search") }}
            <span
              v-if="foundItems > 0"
              class="text-grey-60 font-weight-semi-bold text-small"
            >
              {{ foundItems }}
              {{
                foundItems > 1
                  ? $t("components.blocks.searchBlock.items")
                  : $t("components.blocks.searchBlock.item")
              }}
            </span>
          </div>
        </action-header>
        <action-body class="px-2 pb-2">
          <v-search
            v-model="value"
            :key="searchKey"
            autofocus
            :placeholder="$t('components.blocks.searchBlock.searchPlaceholder')"
            class="mb-4"
            @clear="() => (value = null)"
          />

          <tag-filter-list
            v-if="withDisplayTags"
            :tags="displayTags"
            class="mb-3"
            @updated="tags => updateDisplayTags(tags)"
          />

          <div
            v-if="isLoading"
            class="d-flex justify-content-center align-items-center my-5 py-5 text-grey-50"
          >
            <v-loader thin />
          </div>
          <div
            v-else-if="errorMessage"
            class="empty-state d-flex align-items-center flex-column py-5 mt-5"
          >
            <v-icon icon="search_off" size="3rem" class="text-grey-50" />
            <div class="text-grey-70 mt-2 font-weight-semi-bold text-center">
              {{ errorMessage }}
            </div>
          </div>
          <div v-else-if="fetchedItems.length">
            <div
              v-for="item in fetchedItems"
              :key="item.id"
              class="search-item m"
            >
              <collection-item
                :to="item.path"
                :page-id="item.page_id"
                :title="item.title"
                :type="item.content_type"
                :content-type="item.content_type"
                :image="item.banner"
                :video="item.video"
                :item-data="item"
                :show-countdown="false"
                :current-schedule="{}"
                card-display="horizontal"
                card-style="raised"
                class="w-100 mb-2"
              />
            </div>

            <infinite-pagination
              v-if="hasMore"
              list-key="items"
              path="/search"
              :init-page="2"
              :query-params="searchParams"
              @loaded="handleLoadedMore"
            />
          </div>
          <div
            v-else
            class="empty-state d-flex align-items-center flex-column py-5 mt-5"
          >
            <v-icon icon="search_off" size="3rem" class="text-grey-50" />
            <div class="text-grey-70 mt-2 font-weight-semi-bold text-center">
              {{ $t("components.blocks.searchBlock.couldNotFind") }}
            </div>
          </div>
        </action-body>
      </template>
    </v-modal>
  </div>
</template>

<script>
import ApiClient from "@/lib/api-client"

import { toPadding } from "@shared/block-ratio"
import debounce from "@/lib/debounce"

import vSearch from "@/components/forms/vSearch"
import ActionHeader from "@/components/modals/shared/ActionHeader"
import ActionBody from "@/components/modals/shared/ActionBody"

import CollectionItem from "@/components/CollectionItem"
import InfinitePagination from "@/components/InfinitePagination"

export default {
  components: {
    vSearch,
    ActionHeader,
    ActionBody,
    CollectionItem,
    InfinitePagination,
    TagFilterList: () => import("@/components/search/TagFilterList")
  },

  props: {
    pageId: { type: [String, Number], default: null },
    contentTypes: { type: Array, default: () => [] },
    withTags: { type: Array, default: () => [] },
    displayTags: { type: Array, default: () => [] },
    withDisplayTags: { type: Boolean, default: false },
    withAllTags: { type: Boolean, default: false },
    spaceXRatio: { type: Number, default: 0 },
    spaceTopRatio: { type: Number, default: 0 },
    spaceBottomRatio: { type: Number, default: 0 }
  },

  data() {
    return {
      value: null,
      searchKey: 1,
      fetchedItems: [],
      foundItems: 0,
      isLoading: false,
      hasMore: false,
      errorMessage: null,
      searchHash: `#search-${this._uid}`,
      filterTags: []
    }
  },

  computed: {
    blockPadding() {
      return toPadding(
        this.spaceTopRatio,
        this.spaceXRatio,
        this.spaceBottomRatio
      )
    },

    searchParams() {
      return {
        term: this.value,
        content_types: this.contentTypes,
        with_tags: this.withTags.map(tag => tag.text).concat(this.filterTags),
        with_all_tags: this.withAllTags,
        page_id: this.pageId
      }
    }
  },

  watch: {
    value() {
      debounce(this.handleSearch, 600)
    }
  },

  methods: {
    openSearchModal() {
      this.$router.push(this.searchHash)
    },

    handleSearch() {
      this.isLoading = true

      ApiClient.get(`/search`, { params: this.searchParams })
        .then(({ data }) => {
          this.foundItems = data.found
          this.fetchedItems = data.items
          this.hasMore = data.has_more
          this.errorMessage = null
        })
        .catch(({ response }) => {
          if (response && response.data?.status == 403) {
            this.errorMessage = this.$t(
              "components.blocks.searchBlock.appNotSupported"
            )
          } else {
            this.$notify(
              this.$t("components.blocks.searchBlock.anErrorOccurred")
            )
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    handleModalOpened() {
      if (this.fetchedItems.length > 0) return

      this.handleSearch()
      this.searchKey++
    },

    handleLoadedMore({ list }) {
      this.fetchedItems = [...this.fetchedItems, ...list]
    },

    updateDisplayTags(tags) {
      this.filterTags = tags
      this.handleSearch()
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/mixins";

.search-block__button {
  @include clickable(0.99);
}
</style>
